import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  MusicNotesIcon,
  PlayIcon,
  ShuffleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { OnScreenKeyboard } from '../OnScreenKeyboard';
import { ModalCloseButton } from '../ModalCloseButton';
import { apiFetch } from '../../../auth/apiFetch';
import { useScrollFade } from '../useScrollFade';

// Music-picker modal for the kiosk. Opened from the launcher buttons (idle)
// and from the now-playing card (to switch without stopping). Unified across
// providers — Music Assistant blends Spotify / Apple Music, so this shows your
// playlists, recently-played tracks, and a global search, all in one place.
//
// Data comes through the shared /api/ha proxy via Music Assistant's read-only
// browse actions (music_assistant.search / get_library). Tapping a result
// plays it on the kiosk player via media_player.play_media (the uri MA hands
// back doubles as the media_content_id).

const SEARCH_DEBOUNCE_MS = 350;
const RESULT_LIMIT = 50;
// Max tracks queued when tapping a song mid-list (play-from-here). Bounds the
// play_media request body on very large playlists; 250 is far more than a
// kitchen session needs in one go.
const QUEUE_CAP = 250;
// Columns per content type — this is the tile-size knob (more columns = smaller
// tiles). Each category previews 2 rows, so its preview count = columns × 2.
// "View more" drills into the full list at the same column count. Tweak freely.
const COLUMNS: Record<string, number> = {
  artists: 7,
  tracks: 6,
  albums: 6,
  playlists: 6,
};
const DEFAULT_COLUMNS = 5;
function columnsFor(key: string): number {
  return COLUMNS[key] ?? DEFAULT_COLUMNS;
}

// Preview rows per content type. Preview count = columns × rows; "View more"
// shows when a category has more than that. Tweak per type to taste.
const ROWS: Record<string, number> = {
  artists: 1,
  tracks: 2,
  albums: 2,
  playlists: 2,
};
const DEFAULT_ROWS = 2;
function rowsFor(key: string): number {
  return ROWS[key] ?? DEFAULT_ROWS;
}

// Section order for both the browse home and search results. Reorder here to
// change both at once.
const SECTION_ORDER = ['artists', 'tracks', 'albums', 'playlists'];
// Library media types we load for the browse-everything home (each ordered by
// most-recently-played so recents float to the top).
const LIBRARY_TYPES = ['playlist', 'album', 'artist', 'track'];
const TYPE_TO_KEY: Record<string, string> = {
  playlist: 'playlists',
  album: 'albums',
  artist: 'artists',
  track: 'tracks',
};
// One-tap mood chips — each runs a PLAYLIST-only search for the keyword.
// Tuned for the kitchen: cooking / dining / gathering contexts that also map to
// well-populated Spotify playlists.
const MOODS = [
  'Jazz',
  'Acoustic',
  'Latin',
  'Italian',
  'Pop',
  'Romantic',
  'Soul',
  'Funk',
  'French',
  'Throwback',
  'Lo-fi',
  'Feel Good',
  'Dance',
];
// How many top (most-recently-played) items per category seed the recents set
// that floats matching items to the top of search results.
const RECENTS_PER_CATEGORY = 12;

// Edge-fade mask for the mood-chip scroller — fades only the side(s) that have
// hidden chips (set by the scroll handler).
function chipMaskStyle({ left, right }: { left: boolean; right: boolean }): CSSProperties {
  if (!left && !right) return {};
  const w = '2.5rem';
  const grad = `linear-gradient(to right, ${[
    left ? 'transparent 0' : 'black 0',
    `black ${w}`,
    `black calc(100% - ${w})`,
    right ? 'transparent 100%' : 'black 100%',
  ].join(', ')})`;
  return { maskImage: grad, WebkitMaskImage: grad };
}

// Float items the user recently played (via MA) to the front of a list.
function bumpRecents(items: MediaItem[], recent: Set<string>): MediaItem[] {
  if (!recent.size) return items;
  const hit: MediaItem[] = [];
  const rest: MediaItem[] = [];
  for (const it of items) (recent.has(it.uri) ? hit : rest).push(it);
  return hit.length ? [...hit, ...rest] : items;
}

interface MediaItem {
  uri: string;
  name: string;
  mediaType: string;
  subtitle?: string;
  image?: string;
}

type Raw = Record<string, unknown>;

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v ? v : undefined;
}

function asArray(v: unknown): Raw[] {
  return Array.isArray(v) ? (v.filter((x) => x && typeof x === 'object') as Raw[]) : [];
}

// Pull a usable artwork URL out of MA's varied item shapes. Only accept https
// (public CDN art for Spotify/Apple loads directly on the HTTPS kiosk); skip
// http/LAN art rather than trip mixed-content — those fall back to a placeholder.
function extractImage(raw: Raw): string | undefined {
  const meta = raw.metadata as Raw | undefined;
  const images = asArray(meta?.images);
  const candidates = [
    asString(raw.image),
    asString(raw.image_url),
    ...images.map((img) => asString(img.path)),
  ];
  return candidates.find((c) => c?.startsWith('https://'));
}

function subtitleFor(raw: Raw): string | undefined {
  const artists = asArray(raw.artists)
    .map((a) => asString(a.name))
    .filter(Boolean);
  if (artists.length) return artists.join(', ');
  return asString(raw.owner) ?? asString((raw.album as Raw | undefined)?.name);
}

function normalizeItem(raw: Raw): MediaItem | null {
  const uri = asString(raw.uri);
  const name = asString(raw.name);
  if (!uri || !name) return null;
  return {
    uri,
    name,
    mediaType: asString(raw.media_type) ?? 'track',
    subtitle: subtitleFor(raw),
    image: extractImage(raw),
  };
}

// Unwrap HA's return_response envelope: the proxy hands back { ok, response },
// and HA's REST wraps the action's data under `service_response`.
function unwrap(json: unknown): Raw {
  const response = (json as Raw | undefined)?.response as Raw | undefined;
  const svc = response?.service_response as Raw | undefined;
  return svc ?? response ?? {};
}

// Pull the proxy's `{ error }` body out of a failed response so we surface
// HA's actual message (e.g. a bad param) instead of a bare status code.
async function errMsg(res: Response): Promise<string> {
  try {
    const j = (await res.json()) as { error?: unknown };
    return typeof j.error === 'string' ? j.error : `ha ${res.status}`;
  } catch {
    return `ha ${res.status}`;
  }
}

async function maAction(
  label: string,
  service: 'search' | 'get_library',
  configEntryId: string,
  params: Record<string, unknown>,
): Promise<Raw> {
  const res = await apiFetch('/api/ha', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      domain: 'music_assistant',
      service,
      config_entry_id: configEntryId,
      ...params,
    }),
  });
  if (!res.ok) throw new Error(`${label}: ${await errMsg(res)}`);
  return unwrap(await res.json());
}

async function playItem(playerEntity: string, item: MediaItem): Promise<void> {
  // Use MA's own action (media_id = the item uri) rather than the generic
  // media_player.play_media, which 500s on Music Assistant uris.
  const res = await apiFetch('/api/ha', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      domain: 'music_assistant',
      service: 'play_media',
      entity_id: playerEntity,
      media_id: item.uri,
      media_type: item.mediaType,
    }),
  });
  if (!res.ok) throw new Error(`play: ${await errMsg(res)}`);
}

// Play an explicit ordered list of track uris, replacing the current queue.
// MA's play_media accepts a list for media_id; `enqueue: 'replace'` starts a
// fresh queue from it. Used for "play album/playlist from this track".
async function playUris(playerEntity: string, uris: string[]): Promise<void> {
  if (!uris.length) return;
  const res = await apiFetch('/api/ha', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      domain: 'music_assistant',
      service: 'play_media',
      entity_id: playerEntity,
      media_id: uris,
      enqueue: 'replace',
    }),
  });
  if (!res.ok) throw new Error(`play: ${await errMsg(res)}`);
}

// Set the player's shuffle flag. Called before play_media so a freshly built
// queue loads (un)shuffled and "next track" follows suit. Best-effort — a
// failure here shouldn't block the play that follows.
async function setShuffle(playerEntity: string, on: boolean): Promise<void> {
  try {
    await apiFetch('/api/ha', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        domain: 'media_player',
        service: 'shuffle_set',
        entity_id: playerEntity,
        shuffle: on,
      }),
    });
  } catch {
    /* non-fatal — proceed with playback regardless */
  }
}

// Normalize a browse_media child (from /api/ha?browse=) into a MediaItem. The
// proxy returns HA BrowseMedia nodes: { title, media_content_id, media_class,
// thumbnail }. Tracks carry an "Artist - Title" title; keep the whole thing as
// the name (the list view shows one line per track).
function normalizeChild(raw: Raw): MediaItem | null {
  const uri = asString(raw.media_content_id);
  const name = asString(raw.title);
  if (!uri || !name) return null;
  const img = asString(raw.thumbnail);
  return {
    uri,
    name,
    mediaType: asString(raw.media_class) ?? 'track',
    image: img?.startsWith('https://') ? img : undefined,
  };
}

// Fetch the tracks inside an album or playlist via the browse proxy.
async function browseTracks(
  playerEntity: string,
  item: MediaItem,
): Promise<MediaItem[]> {
  const res = await apiFetch(
    `/api/ha?browse=${encodeURIComponent(item.uri)}&type=${encodeURIComponent(
      item.mediaType,
    )}&player=${encodeURIComponent(playerEntity)}`,
  );
  if (!res.ok) throw new Error(`browse: ${await errMsg(res)}`);
  const json = (await res.json()) as Raw;
  return asArray(json.children).map(normalizeChild).filter(Boolean) as MediaItem[];
}

// Fetch an artist's songs. Browsing an artist via MA returns their *albums*,
// not tracks, so for the "their songs" view we run a track-scoped search
// filtered to the artist instead.
async function artistTracks(
  configEntryId: string,
  item: MediaItem,
): Promise<MediaItem[]> {
  const data = await maAction('artist tracks', 'search', configEntryId, {
    name: item.name,
    media_type: 'track',
    artist: item.name,
    limit: RESULT_LIMIT,
  });
  return asArray(data.tracks).map(normalizeItem).filter(Boolean) as MediaItem[];
}

interface MusicBrowserModalProps {
  open: boolean;
  onClose: () => void;
  playerEntity: string;
  configEntryId: string;
  /** Fired after a successful play so the panel can refresh its now-playing state. */
  onPlayed?: () => void;
}

export function MusicBrowserModal({
  open,
  onClose,
  playerEntity,
  configEntryId,
  onPlayed,
}: MusicBrowserModalProps) {
  const [query, setQuery] = useState('');
  // On-screen keyboard (the kiosk has no OS virtual keyboard). Opens when the
  // search field is focused/tapped.
  const [kbOpen, setKbOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Active mood chip (a keyword) — runs a playlist-only search. null = normal.
  const [mood, setMood] = useState<string | null>(null);
  // Browse-everything home: media-type key → items (each ordered recents-first).
  const [library, setLibrary] = useState<Record<string, MediaItem[]>>({});
  const [results, setResults] = useState<Record<string, MediaItem[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // URIs of recently-played items (top of each library list) — used to float
  // matching items to the front of search results.
  const recentUris = useRef<Set<string>>(new Set());
  // Drill-down: when set, the modal shows the full list for one category.
  const [expanded, setExpanded] = useState<{
    title: string;
    items: MediaItem[];
    cols: number;
  } | null>(null);
  // Item detail: tapping an artist / album / playlist opens its track list with
  // play + shuffle. `tracks` is null while loading.
  const [detail, setDetail] = useState<{
    item: MediaItem;
    tracks: MediaItem[] | null;
    error: string | null;
  } | null>(null);
  const alive = useRef(true);
  // Mood-chip scroller: track which edge(s) have hidden chips to fade them.
  const chipScrollRef = useRef<HTMLDivElement>(null);
  const [chipFade, setChipFade] = useState({ left: false, right: false });
  const updateChipFade = useCallback(() => {
    const el = chipScrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setChipFade((p) => (p.left === left && p.right === right ? p : { left, right }));
  }, []);
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(updateChipFade);
    return () => cancelAnimationFrame(id);
  }, [open, expanded, updateChipFade]);

  // Load playlists + recently-played once when the modal opens.
  useEffect(() => {
    if (!open) return;
    alive.current = true;
    setError(null);
    setExpanded(null);
    setDetail(null);
    setKbOpen(false);
    if (!configEntryId) {
      setError('Music Assistant not configured (missing config_entry_id)');
      return;
    }
    setLibrary({});
    void (async () => {
      // Browse-everything: load each library type ordered by last-played so
      // recents float to the top. allSettled so one type failing doesn't blank
      // the rest.
      const settled = await Promise.allSettled(
        LIBRARY_TYPES.map((mt) =>
          maAction(mt, 'get_library', configEntryId, {
            media_type: mt,
            order_by: 'last_played_desc',
            limit: 100,
          }),
        ),
      );
      if (!alive.current) return;
      const lib: Record<string, MediaItem[]> = {};
      const recent = new Set<string>();
      const fails: string[] = [];
      settled.forEach((res, i) => {
        const key = TYPE_TO_KEY[LIBRARY_TYPES[i]];
        if (res.status === 'fulfilled') {
          const items = asArray(res.value.items ?? res.value)
            .map(normalizeItem)
            .filter(Boolean) as MediaItem[];
          lib[key] = items;
          for (const it of items.slice(0, RECENTS_PER_CATEGORY)) recent.add(it.uri);
        } else {
          fails.push(res.reason instanceof Error ? res.reason.message : String(res.reason));
        }
      });
      setLibrary(lib);
      recentUris.current = recent;
      setError(fails.length ? fails.join(' · ') : null);
    })();
    return () => {
      alive.current = false;
    };
  }, [open, configEntryId]);

  // Debounced global search.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      setResults({});
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params: Record<string, unknown> = { name: q, limit: RESULT_LIMIT };
        // Mood chips search playlists only.
        if (mood) params.media_type = 'playlist';
        const data = await maAction('search', 'search', configEntryId, params);
        if (!alive.current) return;
        const grouped: Record<string, MediaItem[]> = {};
        for (const key of SECTION_ORDER) {
          const items = asArray(data[key]).map(normalizeItem).filter(Boolean) as MediaItem[];
          if (items.length) grouped[key] = bumpRecents(items, recentUris.current);
        }
        setResults(grouped);
        setError(null);
      } catch (e) {
        if (alive.current) setError(e instanceof Error ? e.message : 'search failed');
      } finally {
        if (alive.current) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, mood, open, configEntryId]);

  // Run a play action, then refresh now-playing and close the modal. Shared by
  // every play path (single item, container play/shuffle, track-from-list).
  const finishPlay = useCallback(
    (p: Promise<void>) => {
      p.then(() => {
        onPlayed?.();
        onClose();
      }).catch((e) => setError(e instanceof Error ? e.message : 'play failed'));
    },
    [onPlayed, onClose],
  );

  // Tapping a tile: tracks play immediately; artists / albums / playlists open
  // a detail view listing their songs (with play + shuffle).
  const onPick = useCallback(
    (item: MediaItem) => {
      setKbOpen(false);
      if (item.mediaType === 'track') {
        finishPlay(playItem(playerEntity, item));
        return;
      }
      setDetail({ item, tracks: null, error: null });
      void (async () => {
        try {
          const tracks =
            item.mediaType === 'artist'
              ? await artistTracks(configEntryId, item)
              : await browseTracks(playerEntity, item);
          if (!alive.current) return;
          setDetail((d) => (d && d.item.uri === item.uri ? { ...d, tracks } : d));
        } catch (e) {
          if (!alive.current) return;
          const msg = e instanceof Error ? e.message : 'failed to load';
          setDetail((d) =>
            d && d.item.uri === item.uri ? { ...d, tracks: [], error: msg } : d,
          );
        }
      })();
    },
    [playerEntity, configEntryId, finishPlay],
  );

  // Detail-view "Play" / "Shuffle": play the container itself (the album /
  // playlist / artist uri), setting shuffle first so a fresh queue loads
  // (un)shuffled. Uses the container uri rather than the loaded track list, so
  // the buttons work the instant the detail view opens and the request stays
  // tiny regardless of how many tracks the container holds.
  const playContainer = useCallback(
    (item: MediaItem, shuffle: boolean) => {
      finishPlay(
        setShuffle(playerEntity, shuffle).then(() => playItem(playerEntity, item)),
      );
    },
    [playerEntity, finishPlay],
  );

  // On-screen keyboard edits — mirror the input's onChange (a search edit
  // resets the active mood chip and any drill-down).
  const typeKey = useCallback((ch: string) => {
    setQuery((q) => q + ch);
    setMood(null);
    setExpanded(null);
  }, []);
  const backspace = useCallback(() => setQuery((q) => q.slice(0, -1)), []);

  const searching = query.trim().length > 0;

  // Soft, time-based top/bottom edge fades for each scrollable view (shared
  // hook). Declared BEFORE every early return below — including `if (!open)` —
  // so the hook order stays stable across renders whether the modal is
  // open/closed or in a drill-down branch. Only the rendered branch's is
  // attached. (Moving these after an early return breaks the Rules of Hooks.)
  const detailFade = useScrollFade([detail]);
  const expandedFade = useScrollFade([expanded]);
  const resultsFade = useScrollFade([results, searching, mood]);

  if (!open) return null;

  const sectionLabels: Record<string, string> = {
    playlists: 'Playlists',
    tracks: 'Tracks',
    albums: 'Albums',
    artists: 'Artists',
  };

  // Item detail: an artist / album / playlist's songs. A left hero rail (cover
  // art + name + Play/Shuffle) sits beside a multi-column, scrolling song list
  // so the wide kiosk screen is filled instead of one narrow column.
  if (detail) {
    const tracks = detail.tracks;
    const uris = tracks?.map((t) => t.uri) ?? [];
    const isArtist = detail.item.mediaType === 'artist';
    const count = tracks?.length ?? 0;
    const meta = [
      detail.item.subtitle,
      count ? `${count} ${count === 1 ? 'song' : 'songs'}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
    return (
      <div className="fixed inset-0 z-50 flex flex-col gap-6 bg-canvas/95 backdrop-blur-sm px-6 py-6">
        {/* Top bar: back + close */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => setDetail(null)}
            aria-label="Back"
            className="shrink-0 grid place-items-center w-12 h-12 rounded-full bg-elevated border border-border text-fg transition-transform active:scale-[0.94]"
          >
            <ArrowLeftIcon size={22} weight="bold" />
          </button>
          <div className="flex-1" />
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Body: hero rail + song list */}
        <div className="flex-1 min-h-0 flex gap-8">
          {/* Left rail: art, title/meta, transport */}
          <div className="w-72 shrink-0 flex flex-col gap-5">
            <div
              className={`w-full aspect-square bg-subtle border border-border overflow-hidden grid place-items-center ${
                isArtist ? 'rounded-full' : 'rounded-2xl'
              }`}
            >
              {detail.item.image ? (
                <img src={detail.item.image} alt="" className="h-full w-full object-cover" />
              ) : (
                <MusicNotesIcon size={64} weight="fill" className="text-fg-tertiary" />
              )}
            </div>
            <div className="min-w-0">
              <h2 className="text-2xl font-semibold leading-tight line-clamp-2">
                {detail.item.name}
              </h2>
              {meta && (
                <div className="mt-1 text-sm text-fg-secondary truncate">{meta}</div>
              )}
            </div>
            {/* Play / Shuffle — play the whole container; work immediately,
                no need to wait for the track list. */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => playContainer(detail.item, false)}
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full bg-elevated border border-border text-fg text-base font-semibold transition-transform active:scale-[0.97]"
              >
                <PlayIcon size={20} weight="fill" />
                Play
              </button>
              <button
                type="button"
                onClick={() => playContainer(detail.item, true)}
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full bg-elevated border border-border text-fg text-base font-semibold transition-transform active:scale-[0.97]"
              >
                <ShuffleIcon size={20} weight="bold" />
                Shuffle
              </button>
            </div>
          </div>

          {/* Right: scrolling, multi-column song list */}
          <div
            ref={detailFade.ref}
            onScroll={detailFade.onScroll}
            style={detailFade.style}
            className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {tracks === null ? (
              <div className="text-sm text-fg-secondary">Loading songs…</div>
            ) : tracks.length === 0 ? (
              <div className="text-sm text-fg-secondary">
                {detail.error ?? 'No songs found.'}
              </div>
            ) : (
              <TrackList
                tracks={tracks}
                // Tap a song → play from there, in order (clear any prior
                // shuffle). Cap the queued slice so the request stays within the
                // proxy's body limit on huge playlists.
                onPick={(i) =>
                  finishPlay(
                    setShuffle(playerEntity, false).then(() =>
                      playUris(playerEntity, uris.slice(i, i + QUEUE_CAP)),
                    ),
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Drill-down: full list for one category.
  if (expanded) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col gap-4 bg-canvas/95 backdrop-blur-sm px-6 pt-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setExpanded(null)}
            aria-label="Back"
            className="shrink-0 grid place-items-center w-12 h-12 rounded-full bg-elevated border border-border text-fg transition-transform active:scale-[0.94]"
          >
            <ArrowLeftIcon size={22} weight="bold" />
          </button>
          <h2 className="flex-1 text-xl font-semibold truncate">{expanded.title}</h2>
          <ModalCloseButton onClick={onClose} />
        </div>
        <div
          ref={expandedFade.ref}
          onScroll={expandedFade.onScroll}
          style={expandedFade.style}
          className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          <Grid items={expanded.items} onPick={onPick} cols={expanded.cols} />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col gap-10 bg-canvas/95 backdrop-blur-sm px-6 pt-6">
      <div className="flex flex-col gap-4 shrink-0">
      {/* Header: search + close */}
      <div className="flex items-center gap-6">
        <div className="flex-1 flex items-center gap-3 card !py-3">
          <MagnifyingGlassIcon size={22} className="shrink-0 text-fg-secondary" />
          <input
            ref={inputRef}
            // We provide our own on-screen keyboard; suppress any native one.
            inputMode="none"
            value={query}
            onFocus={() => setKbOpen(true)}
            // Tapping away hides the keyboard; tapping keys keeps focus
            // (they preventDefault), so this only fires on a real tap-out.
            onBlur={() => setKbOpen(false)}
            onChange={(e) => {
              setQuery(e.target.value);
              setMood(null);
              setExpanded(null);
            }}
            placeholder="Search songs, artists, albums…"
            className="flex-1 bg-transparent outline-none text-lg text-fg placeholder:text-fg-tertiary"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setMood(null);
              }}
              className="shrink-0 -my-1 h-7 px-4 inline-flex items-center rounded-full bg-elevated border border-border text-sm text-fg-secondary transition-transform active:scale-[0.97]"
            >
              Clear
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 grid place-items-center w-12 h-12 rounded-full bg-elevated border border-border text-fg transition-transform active:scale-[0.94]"
        >
          <XIcon size={22} weight="bold" />
        </button>
      </div>
        {/* Mood chips — one-tap playlist-only search by keyword. Horizontally
            scrollable so the row stays one line however many chips there are. */}
        <div
          ref={chipScrollRef}
          onScroll={updateChipFade}
          style={chipMaskStyle(chipFade)}
          className="flex gap-3 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {MOODS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setQuery(m);
                setMood(m);
                setExpanded(null);
              }}
              className={`shrink-0 h-9 px-4 inline-flex items-center rounded-full border text-sm font-medium transition-colors active:scale-[0.97] ${
                mood === m
                  ? 'bg-fg text-canvas border-transparent'
                  : 'bg-elevated text-fg-secondary border-border'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="text-sm text-red shrink-0">{error}</div>}

      <div
        ref={resultsFade.ref}
        onScroll={resultsFade.onScroll}
        style={resultsFade.style}
        className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-10 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {searching ? (
          <>
            {loading && Object.keys(results).length === 0 && (
              <div className="text-sm text-fg-secondary">Searching…</div>
            )}
            {!loading && Object.keys(results).length === 0 && (
              <div className="text-sm text-fg-secondary">No results.</div>
            )}
            {mood ? (
              // Mood search is playlists-only — show the full grid, no preview.
              <Grid
                items={results.playlists ?? []}
                onPick={onPick}
                cols={columnsFor('playlists')}
              />
            ) : (
              Object.entries(results).map(([key, items]) => (
                <CategorySection
                  key={key}
                  title={sectionLabels[key] ?? key}
                  items={items}
                  cols={columnsFor(key)}
                  rows={rowsFor(key)}
                  onPick={onPick}
                  onViewMore={() =>
                    setExpanded({
                      title: sectionLabels[key] ?? key,
                      items,
                      cols: columnsFor(key),
                    })
                  }
                />
              ))
            )}
          </>
        ) : Object.keys(library).length ? (
          SECTION_ORDER.filter((key) => (library[key]?.length ?? 0) > 0).map((key) => (
            <CategorySection
              key={key}
              title={sectionLabels[key] ?? key}
              items={library[key]}
              cols={columnsFor(key)}
              rows={rowsFor(key)}
              onPick={onPick}
              onViewMore={() =>
                setExpanded({
                  title: sectionLabels[key] ?? key,
                  items: library[key],
                  cols: columnsFor(key),
                })
              }
            />
          ))
        ) : (
          <div className="text-sm text-fg-secondary">
            {error ? '—' : 'Loading your library…'}
          </div>
        )}
      </div>

      {kbOpen && (
        <OnScreenKeyboard
          onKey={typeKey}
          onBackspace={backspace}
          onClose={() => {
            setKbOpen(false);
            inputRef.current?.blur();
          }}
        />
      )}
    </div>
  );
}

function CategorySection({
  title,
  items,
  cols,
  rows,
  onPick,
  onViewMore,
}: {
  title: string;
  items: MediaItem[];
  cols: number;
  rows: number;
  onPick: (i: MediaItem) => void;
  onViewMore: () => void;
}) {
  const previewCount = cols * rows;
  const hasMore = items.length > previewCount;
  return (
    <section className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-2xl text-fg">{title}</h3>
        {hasMore && (
          <button
            type="button"
            onClick={onViewMore}
            className="shrink-0 px-4 py-1.5 rounded-full bg-elevated border border-border text-sm text-fg-secondary transition-transform active:scale-[0.97]"
          >
            View more
          </button>
        )}
      </div>
      <Grid items={items.slice(0, previewCount)} onPick={onPick} cols={cols} />
    </section>
  );
}

function Grid({
  items,
  onPick,
  cols,
}: {
  items: MediaItem[];
  onPick: (i: MediaItem) => void;
  cols: number;
}) {
  return (
    <div
      className="grid gap-6"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {items.map((item) => (
        <button
          key={item.uri}
          type="button"
          onClick={() => onPick(item)}
          className={`flex flex-col gap-2 transition-transform active:scale-[0.97] ${
            item.mediaType === 'artist' ? 'text-center' : 'text-left'
          }`}
        >
          <div
            className={`aspect-square w-full bg-subtle border border-border overflow-hidden grid place-items-center ${
              item.mediaType === 'artist' ? 'rounded-full' : 'rounded-xl'
            }`}
          >
            {item.image ? (
              <img src={item.image} alt="" className="h-full w-full object-cover" />
            ) : (
              <MusicNotesIcon size={32} weight="fill" className="text-fg-tertiary" />
            )} 
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight truncate">{item.name}</div>
            {item.subtitle && (
              <div className="text-xs text-fg-secondary leading-tight truncate">
                {item.subtitle}
              </div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

// Single-column song list for the detail view — one tappable row per track
// (number + title). Tapping a row plays the list from that track onward.
function TrackList({
  tracks,
  onPick,
}: {
  tracks: MediaItem[];
  onPick: (index: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      {tracks.map((track, i) => (
        <button
          key={`${track.uri}-${i}`}
          type="button"
          onClick={() => onPick(i)}
          className="flex items-center gap-4 px-3 py-3 rounded-xl text-left transition-colors active:bg-elevated"
        >
          <span className="w-6 shrink-0 text-right text-sm tabular-nums text-fg-tertiary">
            {i + 1}
          </span>
          <span className="flex-1 min-w-0 text-base text-fg leading-tight truncate">
            {track.name}
          </span>
          <PlayIcon size={18} weight="fill" className="shrink-0 text-fg-tertiary" />
        </button>
      ))}
    </div>
  );
}
